import React, { FC, MouseEvent, ReactNode, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form, Modal } from 'antd';

// Shared UI Library Components
import {
  ChipsInput,
  CustomButton,
  CustomIcon,
  CustomInput,
  EIconName
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './update-specialty-specialisations-modal.scss';

// Store entities
import {
  createCompanySpecialtySpecialisations,
  updateCompanySpecialtySpecialisations
} from '../../store/company/company.thunks';

// Models
import { ISpecialtySpecialisations } from '../../interfaces/company.interfaces';

type TFormValues = {
  specialty: string;
  specialisations: string[];
};

type TUpdateSpecialtySpecialisationsModalProps = {
  open: boolean;
  info: ISpecialtySpecialisations | null;
  onSubmit?: (info: ISpecialtySpecialisations) => void;
  onCancel?: () => void;
}

export const UpdateSpecialtySpecialisationsModal: FC<TUpdateSpecialtySpecialisationsModalProps> = (
  {
    open = false,
    info = null,
    onSubmit = () => {
    },
    onCancel = () => {
    }
  }: TUpdateSpecialtySpecialisationsModalProps
) => {
  const [form] = Form.useForm<TFormValues>();
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [disabledSpecialisationLength, setDisabledSpecialisationLength] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const dispatch = useDispatch<any>();

  const isEditState = !!info;

  const handleCancel = (_: MouseEvent<HTMLButtonElement>): void => {
    onCancel();
    setHasChanges(false);
    form.resetFields();
  };

  const generateOptions = (options: {name: string, id?: number, code?: string}[]): {
    label: ReactNode,
    value: string,
    disabled?: boolean,
  }[] => {
    return options.map((option) => (
      {label: option.name, value: option.name})
    )
  };

  const specialisationOptions = isEditState ? generateOptions(info?.specializations) : [];

  const createSpecialtySpecialisations = async ({
                                                  specialty: speciality_name,
                                                  specialisations
                                                }: TFormValues): Promise<void> => {
    const {payload} = await dispatch(createCompanySpecialtySpecialisations({
      speciality_name,
      specializations: specialisations?.map(name => ({name}))
    }));

    setSavingChanges(false);

    if (payload) {
      form.resetFields();
      onSubmit(payload);
      setHasChanges(false);
    }
  }

  const updateSpecialtySpecialisations = async ({
                                                  specialty: speciality_name,
                                                  specialisations
                                                }: TFormValues): Promise<void> => {
    const {payload} = await dispatch(updateCompanySpecialtySpecialisations({
      speciality_id: info?.speciality.id ?? 0,
      speciality_name,
      specializations: specialisations?.map(name => ({name}))
    }));

    setSavingChanges(false);

    if (payload) {
      form.resetFields();
      onSubmit(payload);
      setHasChanges(false);
    }
  }

  const handleSubmit = async (): Promise<void> => {
    setSavingChanges(true);
    const fieldsValue = form.getFieldsValue();

    if (info) {
      updateSpecialtySpecialisations(fieldsValue);
      return;
    }
    createSpecialtySpecialisations(fieldsValue);
  }

  const onFieldsChange = (_: { [key in keyof TFormValues]: string | string[] }, {
    specialty,
    specialisations
  }: TFormValues): void => {
    setHasChanges(
      !!specialty?.length && (
        info?.speciality?.name !== specialty ||
        info?.specializations?.some(spec => !specialisations.includes(spec.name)) ||
        specialisations?.length !== info?.specializations?.length
      )
    );
  }

  useEffect(() => {
    if (!info) {
      form.setFieldsValue({specialty: '', specialisations: []});
      return;
    }
    form.setFieldsValue({
      specialty: info?.speciality?.name,
      specialisations: info?.specializations?.map(s => s.name)
    });
  }, [info]);

  return (
    <Modal
      rootClassName="update-specialty-specialisations-modal"
      width={'814px'}
      open={open}
      centered={true}
      closeIcon={false}
      maskClosable={false}
      title={`${info ? 'Edit' : 'Add'} Specialty and Specialisation(s)`}
      footer={[
        <CustomButton key="cancel" text={'Cancel'} disabled={savingChanges} type={'text'}
                      onClick={handleCancel}/>,
        <CustomButton key="save" text={'Save'} disabled={!hasChanges || savingChanges || disabledSpecialisationLength}
                      loading={savingChanges} onClick={handleSubmit}/>
      ]}
      onCancel={handleCancel}
    >
      <Form className="update-specialty-specialisations-modal--body"
            form={form}
            onValuesChange={onFieldsChange}
      >

        <div className="body-item">
          <div className="body-item--title">
            {isEditState ? <p>Refine your Specialty details and manage its linked Specializations</p>
              : <p>Create as many Specialties as needed to provide a detailed description of your Consultants' skills and
                experience. Each Specialty can include multiple Specializations, allowing your clients to find
                specialists in specific areas.</p>}
          </div>
        </div>
        <div className="body-item">
          <Form.Item name="specialty" label={'Specialty'} rules={[{required: true}]}>
            <CustomInput type={'text'} placeholder={'Specialty*'} maxLength={120}/>
          </Form.Item>
        </div>
        <div className="body-item">
          <Form.Item name="specialisations"
                     messageVariables={{fieldLabel: 'specialisations'}}>
            <ChipsInput mode="tags" options={specialisationOptions}
                        hideDropdown={true} validStatus={(validStatus) => setDisabledSpecialisationLength(!validStatus)}
                        placeholder={'Specialisation(s)'} maxTagValueLength={300}
                        hint={'Type and click “Enter” to add a specialisation'}
            />
          </Form.Item>
        </div>
        {isEditState && <div className="body-item body-item--error-message">
          <CustomIcon name={EIconName.Warning}/> Deleting a Specialisation(s) will remove it from the Consultants' Profiles. Please be aware of this before proceeding.
        </div>}
      </Form>
    </Modal>
  )
};
